import type { MutableRefObject, RefObject } from 'react';
import { useEffect, useMemo } from 'react';

const trackReference = (
    target: RefObject<any>,
    interactionHandler: (percentage: number, observer: IntersectionObserver) => any,
    topLineOffset: number,
    thresholds: number[],
    rootRef?: MutableRefObject<HTMLElement>
) => {
    if (!target) {
        return;
    }

    const observationHandler: IntersectionObserverCallback = (visualEntries: IntersectionObserverEntry[], observer) => {
        for (const visualEntry of visualEntries) {
            if (visualEntry.target === target.current) {
                interactionHandler(visualEntry.intersectionRatio, observer);
                return;
            }
        }
    };

    const intersectionObserver = new IntersectionObserver(observationHandler, {
        threshold: thresholds,
        rootMargin: `-${topLineOffset}px 0px 0px`,
        root: rootRef?.current,
    });

    if (target.current) {
        intersectionObserver.observe(target.current);
    }

    return () => {
        if (target.current) {
            intersectionObserver.unobserve(target.current);
        }
    };
};

/**
 * A useEffect hook that tracks vertical visibility of an element relative to the viewport or root if used.
 *
 * @param targetElementReference - The target element to check
 * @param interactionHandler - Callback which will receive a ratio and a reference to the observer instance
 * @param thresholdGranularity - How many steps from 0 to 1 to fire a callback: 2 steps means 0 and 1, 3 steps means 0, 0.5, 1
 * @param topLineOffset - How many pixels from the top need to be discounted
 * @param rootRef - Optional parameter to set the root, if not the viewport is used
 */
export const useIntersectionObserver = (
    targetElementReference: RefObject<any>,
    interactionHandler: (ratio: number, observer: IntersectionObserver) => any,
    thresholdGranularity = 101,
    topLineOffset = 0,
    rootRef?: MutableRefObject<HTMLElement>
) => {
    const thresholds = useMemo(() => {
        const limitedThresholdGranularity = Math.max(thresholdGranularity, 2);
        return Array.from({ length: limitedThresholdGranularity }, (_, k) => k / (limitedThresholdGranularity - 1));
    }, [thresholdGranularity]);

    useEffect(() => {
        if ('IntersectionObserver' in window) {
            return trackReference(targetElementReference, interactionHandler, topLineOffset, thresholds, rootRef);
        }
    }, [targetElementReference]);
};
